import { motion } from 'framer-motion';
import { ArrowRight, Phone, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

export function CTASection() {
  const t = useT();
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      {/* Animated Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[100px] animate-pulse-glow" />
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {t('Bereit für Ihr nächstes ', 'Ready for your next ')}
            <span className="text-primary">{t('Projekt?', 'project?')}</span>
          </motion.h2>
          
          <motion.p
            className="mt-6 text-xl text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {t(
              'Ob gedrehte oder gefräste Einzelteile, Kleinserien, komplette Baugruppen oder Vorrichtungen: Sprechen Sie mit uns. Kompetent, unverbindlich und persönlich.',
              'Turned or milled single parts, small series, full assemblies or fixtures — talk to us. Competent, non-binding and personal.',
            )}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button variant="hero" size="xl" className="w-full sm:w-auto" asChild>
              <LocalizedLink to="/kontakt">
                {t('Jetzt Anfrage starten', 'Start your enquiry')}
                <ArrowRight className="w-5 h-5" />
              </LocalizedLink>
            </Button>
            <Button variant="heroOutline" size="xl" className="w-full sm:w-auto" asChild>
              <a href="tel:+4961501849730">
                <Phone className="w-5 h-5" />
                {t('Direkt anrufen', 'Call us directly')}
              </a>
            </Button>
          </motion.div>

          {/* Quick Info */}
          <motion.div
            className="mt-12 flex flex-wrap justify-center gap-8 text-sm text-muted-foreground"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-primary" />
              <span className="font-mono">{t('06150 184973-0', '+49 6150 184973-0')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              <span>{t('Mo-Do 7:30-16:00 | Fr 7:30-12:30', 'Mon-Thu 7:30-16:00 | Fri 7:30-12:30')}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              <span>{t('Darmstadt, Deutschland', 'Darmstadt, Germany')}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
